import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import { toastWarning } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';

import { dashboardComparisonColors, repeatColors } from '@features/crm/utils/helpers';
import MyChart from '@features/crm/v3/dashboard/components/MyChart';
import { generateCRMDashboardParams } from '@features/filter/static/filterUtil';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	compareCRMOverallPerformance,
	getCRMDashboardYears,
	getCRMOverallPerformance
} from '@services/crmService';

const overAllBarList = [
	{
		fill: '#0095FF',
		dataKey: 'target',
		title: 'Target'
	},
	{ fill: '#BA1E45', dataKey: 'unachieved', title: 'Unachieved Previous Target' },
	{ fill: '#F47920', dataKey: 'pipeline', title: 'Pipeline' },
	{ fill: '#00E096', dataKey: 'achieved', title: 'Achieved' }
];

const OverallPerformance = () => {
	const filterFields = useSelector((store) => store.filterFieldStore);

	const [selectedChartType, setSelectedChartType] = useState({ id: 'QUARTERLY', value: 'Quarterly' });
	const [isCompareEnable, setIsCompareEnable] = useState(false);
	const [selectedYears, setSelectedYears] = useState([]);
	const [isShowYears, setIsShowYears] = useState(false);
	const [chartData, setChartData] = useState([]);
	const [years, setYears] = useState([]);
	const [yearWithColor, setYearWithColor] = useState([]);

	const [chartTypes] = useState([
		{ id: 'QUARTERLY', value: 'Quarterly' },
		{ id: 'WEEKLY', value: 'Weekly' },
		{ id: 'MONTHLY', value: 'Monthly' }
	]);
	const yearRef = useRef(null);
	const dispatch = useDispatch();

	const onOutSideClick = () => {
		if (isShowYears) {
			setIsShowYears(false);
		}
	};

	useClickOutside(yearRef, onOutSideClick);

	const generateParams = (isCompareEnable) => {
		if (isCompareEnable) {
			return {
				type: selectedChartType.id,
				yearList: selectedYears
			};
		}
		return {
			type: selectedChartType.id,
			...generateCRMDashboardParams(filterFields)
		};
	};
	const getLabel = (item) => {
		if (selectedChartType.id === 'QUARTERLY') {
			return `${item.quarter}, ${item.year}`;
		}
		if (selectedChartType.id === 'WEEKLY') {
			return `W${item.weekNo}, ${item.year}`;
		}
		if (selectedChartType.id === 'MONTHLY') {
			return `${item.month}, ${item.year}`;
		}
	};

	const generateChartData = (data) => {
		if (!isCompareEnable || selectedYears.length === 0) {
			setYearWithColor([]);
			setChartData([
				...data.map((item) => ({
					name: getLabel(item),
					achieved: item?.achievedAmount || 0,
					pipeline: item?.revenuePipelineAmount || 0,
					target: item?.targetAmount || 0,
					unachieved: item?.unAchievedAmount || 0
				}))
			]);
		}
	};

	const generateChartDataCompare = (data) => {
		if (selectedYears.length > 0) {
			const years = [
				...new Set(
					Object.values(data)
						.flat()
						.map((item) => item.year)
				)
			].sort((a, b) => a - b);

			const yearsWithColors = years.map((year, index) => ({
				year,
				fill: repeatColors(dashboardComparisonColors, selectedYears.length)[index]
			}));
			setYearWithColor(yearsWithColors);
		}

		const mapDataByPeriod = (data, period) => {
			return data.reduce((acc, item) => {
				const { year, achievedAmount } = item;
				const periodName = item[period];
				let existingPeriod = acc.find((entry) => entry.name === periodName);
				if (!existingPeriod) {
					existingPeriod = {
						name: periodName
					};
					acc.push(existingPeriod);
				}
				existingPeriod[year] = achievedAmount;
				return acc;
			}, []);
		};

		const selectedPeriod = {
			QUARTERLY: 'quarter',
			WEEKLY: 'weekNo',
			MONTHLY: 'month'
		}[selectedChartType.id];

		const result = selectedPeriod ? mapDataByPeriod(Object.values(data).flat(), selectedPeriod) : [];

		setChartData(result);
	};

	useEffect(() => {
		if (!isCompareEnable) {
			(async () => {
				try {
					dispatch(openLoader());
					const data = await getCRMOverallPerformance(generateParams(isCompareEnable));
					generateChartData(data);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			})();
		}
	}, [filterFields, selectedChartType, isCompareEnable]);

	useEffect(() => {
		if (selectedYears.length > 0 && isCompareEnable) {
			(async () => {
				try {
					dispatch(openLoader());
					const data = await compareCRMOverallPerformance(generateParams(isCompareEnable));
					generateChartDataCompare(data);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			})();
		}
	}, [filterFields, selectedYears, isCompareEnable, selectedChartType]);

	useEffect(() => {
		if (isCompareEnable) {
			(async () => {
				try {
					dispatch(openLoader());
					let params = generateCRMDashboardParams(filterFields, false);
					delete params.dateRange;
					const data = await getCRMDashboardYears(params);
					setYears(data);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			})();
		} else {
			if (!isShowYears) setSelectedYears([]);
		}
	}, [isCompareEnable, filterFields]);

	const handleItemClick = (data) => {
		setSelectedChartType({ id: data.id, value: data.value });
	};

	const compareEnable = () => {
		setIsCompareEnable((prev) => !prev);
		setYearWithColor([]);
	};

	const renderChartTitles = () => {
		if (yearWithColor.length === 0) {
			return (
				<div className="d-flex flex-wrap gap-6 mr-5">
					{overAllBarList.map((bar) => (
						<div className={`${bar.dataKey} d-flex`} key={`all_bar_${bar.dataKey}`}>
							<span className={`color-indicator ${bar.dataKey} d-flex`} />
							<p className="regular-12 weight-500">{bar.title}</p>
						</div>
					))}
				</div>
			);
		} else {
			return (
				<div className="d-flex gap-6 mr-5">
					{yearWithColor?.map((item) => (
						<div className="target d-flex" key={`year_${item.year}`}>
							<span
								className="color-indicator"
								style={{
									background: item.fill
								}}
							/>
							<p className="regular-12 weight-500">{item.year}</p>
						</div>
					))}
				</div>
			);
		}
	};
	return (
		<div className="overall-performance-container">
			<div className="performance-heading d-flex justify-content-between align-items-center">
				<p className="regular-14 gray_dark_01 weight-500">Overall Performance</p>
				<div className="dot-content-toggle d-flex align-items-center">
					{renderChartTitles()}
					<div className="quarterly-and-compaire d-flex">
						<div className="transparent-select mr-3">
							<CustomDropdown
								type="addItem"
								title="chartType"
								onItemClick={handleItemClick}
								items={chartTypes}
								selectedItem={selectedChartType?.value}
							/>
						</div>
						<div className="d-flex align-items-center cursor-pointer" onClick={compareEnable}>
							<p className="regular-14 gray_dark_01 mr-2">Compare</p>
							<img
								src={
									isCompareEnable
										? `${process.env.PUBLIC_URL}/icons/toggle-on.svg`
										: `${process.env.PUBLIC_URL}/icons/toggle-off.svg`
								}
								alt="arrow"
							/>
						</div>
						{isCompareEnable && (
							<div className="select-component order-column-filter">
								<div
									className="click-box click mb-1"
									onClick={() => setIsShowYears((prev) => !prev)}
								>
									<p className="regular-14 ">
										{yearWithColor.length > 0 ? `${yearWithColor.length} Years` : 'Years'}
									</p>
									<img src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`} alt="" />
								</div>
								<div ref={yearRef}>
									{isShowYears && (
										<div className="dropdown-box">
											{years?.map((item) => (
												<div className="custom-chekbox" key={`year_${item}`}>
													<div className="form-group">
														<input
															type="checkbox"
															id={`overall_year_${item}`}
															onChange={() => {
																if (selectedYears.includes(item)) {
																	setSelectedYears((prev) =>
																		prev.filter((year) => year !== item)
																	);
																} else {
																	if (selectedYears.length < 5) {
																		setSelectedYears((prev) => {
																			const updatedYears = [
																				...prev,
																				item
																			];
																			return updatedYears.sort(
																				(a, b) => a - b
																			); // Sort in ascending order
																		});
																	} else {
																		toastWarning(
																			'Maximum 5 years selectable at a time!'
																		);
																	}
																}
															}}
															checked={selectedYears.includes(item)}
														/>
														<label htmlFor={`overall_year_${item}`}>
															<span>{item}</span>
														</label>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="performance-body">
				<MyChart
					yearWithColor={yearWithColor}
					chartData={chartData}
					maxBarsVisible={16}
					barWidth={100}
					isCompareEnable={isCompareEnable}
					barList={overAllBarList}
					numberOfBars={10}
				/>
			</div>
		</div>
	);
};

export default OverallPerformance;
